import { Icon } from "./icons/Icon"
import { useAtom, useAtomValue, useSetAtom } from "jotai"
import { useTranslation } from "react-i18next"
import clsx from "clsx"
import { Button } from "@/components/ui/Button"
import { Spinner } from "./ui/Spinner"
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
} from "@/components/ui/Select"

import {
	DisplayGroup,
	PhotoFlagStatus,
	Rating,
	SortOption,
	SortOptions,
	Visibility,
} from "@/lib/catalog/types"
import { replaceDialog } from "@/store/dialogs-state"
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuSeparator,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu"
import { ShortCutsDialog } from "./Dialogs/ShortCutsDialog"
import {
	cameraFiltersAtom,
	colorFiltersAtom,
	displayGroupAtom,
	fileFormatFiltersAtom,
	flagFiltersAtom,
	isCalibrationAtom,
	isCullingModeAtom,
	isManualModeAtom,
	pathFiltersAtom,
	projectAtom,
	projectPathsAtom,
	ratingFiltersAtom,
	selectCameraFilter,
	selectColorFilter,
	selectDisplayGroup,
	selectFileFormatFilter,
	selectFlagFilter,
	selectPathFilter,
	selectRatingFilter,
	selectVisibilityFilter,
	updateCurrentProject,
	visibilityFiltersAtom,
} from "@/store/project/projects-state"
import { SettingsDialog } from "./Dialogs/SettingsDialog"
import {
	allCamerasAtom,
	allFileFormatsAtom,
	colorPhotosCountAtom,
	currentPhotoAtom,
	displayGroupPhotosCountAtom,
	displayedPhotosAtom,
	flagPhotosCountAtom,
	isExpressSetupAtom,
	searchInputAtom,
	searchResultPhotoIdsAtom,
	selectedPhotosAtom,
	sortedPhotosAtom,
	starRatingPhotosCountAtom,
	visibilityPhotosCountAtom,
} from "@/store/photo/photos-state"
import { PathInfo } from "@/storage/file-system"
import { Checkbox } from "./ui/Checkbox"
import { ColorTag } from "@/utils/exif"
import { FloatingTooltip } from "./ui/FloatingTooltip"
import {
	cullingHiddenPhotosTooltipAtom,
	isManualCullingAtom,
	isMetadataFinishedAtom,
	markOnboardingShown,
	metadataProgressAtom,
	showingAdvancedSuggestedTooltipAtom,
	showingCullingFilterTooltipAtom,
	showingSmartCullingFiltersTooltipAtom,
	unsupportedPhotoTooltipAtom,
} from "@/store/app-state"
import { IconName } from "./icons/icons"
import { CandidateBadgeIcon, KeeperIcon, RejectBadgeIcon } from "./icons/CullingIcon"
import { filmstripExpandedAtom, showingFilmstripExpanderAtom } from "@/store/ui-views-state"
import { SelectLabel } from "@radix-ui/react-select"
import { DropdownMenuLabel } from "@radix-ui/react-dropdown-menu"
import { displayingFiltersAtom, FilterType, sortOptionAtom } from "@/store/photo/photos-view-state"
import { ReactNode, useEffect, useRef } from "react"
import { useVisibleElementWidth } from "@/utils/useVisibleElement"
import {
	isShowingRejectsAtom,
	isShowingRejectsOrKeepersAtom,
	isSuggestingKeepersAtom,
} from "@/store/importing/culling-state"
import { ContentTooltipWrapper } from "./ui/ContentTooltip"
import {
	colorTagKeys,
	sortOptionKeys,
	displayGroupKeys,
	photoFlagKeys,
} from "@/i18n/dual-role-strings"

const displayingFilterKeys: Record<FilterType, string> = {
	color: "bottomToolBar.filterColor",
	rating: "bottomToolBar.filterRating",
	flag: "bottomToolBar.filterFlag",
	"camera model": "bottomToolBar.filterCameraModel",
	folder: "bottomToolBar.filterFolder",
	"file type": "bottomToolBar.filterFileType",
	visibility: "bottomToolBar.filterVisibility",
}

const visibilityFilterKeys: Record<Visibility, string> = {
	visible: "bottomToolBar.visibilityVisible",
	hidden: "bottomToolBar.visibilityHidden",
}

const PhotoBreadcrumb = ({ maxWidth }: { maxWidth: string }) => {
	const { t } = useTranslation("components")
	const allPhotos = useAtomValue(sortedPhotosAtom)
	const displayedPhotos = useAtomValue(displayedPhotosAtom)
	const selectedPhotos = useAtomValue(selectedPhotosAtom)
	const currentPhoto = useAtomValue(currentPhotoAtom)
	const isMetadataFinished = useAtomValue(isMetadataFinishedAtom)
	const searchInput = useAtomValue(searchInputAtom)
	const searchResults = useAtomValue(searchResultPhotoIdsAtom)
	const visibilityPhotosCount = useAtomValue(visibilityPhotosCountAtom)
	const visibilityFilters = useAtomValue(visibilityFiltersAtom)

	const allCount = allPhotos.length
	const displayCount = isMetadataFinished ? displayedPhotos.length : allCount
	const hasSearch = searchInput.trim().length > 0
	const searchResultsCount = searchResults.size

	let text = ""

	if (hasSearch && searchResultsCount > 0) {
		if (displayCount < searchResultsCount) {
			text = t("bottomToolBar.photoCountFound", {
				displayCount,
				searchResultsCount,
				allCount,
			})
		} else {
			text = t("bottomToolBar.photoCountFoundAll", { searchResultsCount, allCount })
		}
	} else {
		text =
			displayCount < allCount
				? t("bottomToolBar.photoCountPartial", { displayCount, allCount })
				: t("bottomToolBar.photoCountTotal", { allCount })
	}

	if (selectedPhotos.length > 0) {
		text += t("bottomToolBar.selected", { count: selectedPhotos.length })
	}

	if (currentPhoto) {
		text += ` / ${currentPhoto.fileName}`
	}

	const notShownCount = allCount - displayCount
	const totalHiddenCount = visibilityPhotosCount.hidden ?? 0
	const isShowingHidden = visibilityFilters.has("hidden")
	const hiddenNotShownCount = isShowingHidden ? 0 : totalHiddenCount
	const filteredCount = Math.max(0, notShownCount - hiddenNotShownCount)

	const getTooltipText = () => {
		if (notShownCount <= 0) return undefined

		if (hiddenNotShownCount > 0 && filteredCount > 0) {
			return t("bottomToolBar.notShownBoth", {
				count: notShownCount,
				filtered: filteredCount,
				hidden: hiddenNotShownCount,
			})
		} else if (filteredCount > 0) {
			return t("bottomToolBar.notShownFiltered", {
				count: notShownCount,
				filtered: filteredCount,
			})
		} else if (hiddenNotShownCount > 0) {
			return t("bottomToolBar.notShownHidden", {
				count: notShownCount,
				hidden: hiddenNotShownCount,
			})
		}
		return undefined
	}

	return (
		<div className="flex flex-none items-center truncate pl-3 text-xs text-base-100">
			<ContentTooltipWrapper tooltip={getTooltipText()} tooltipProps={{ side: "top" }}>
				<div
					className="hidden overflow-hidden truncate whitespace-nowrap lg:block"
					style={{ maxWidth: maxWidth }}
				>
					{text}
				</div>
			</ContentTooltipWrapper>
		</div>
	)
}

export const BottomToolbar = () => {
	const { t } = useTranslation("components")
	const [sortOption, setSortOption] = useAtom(sortOptionAtom)
	const [displayGroup, setDisplayGroup] = useAtom(displayGroupAtom)
	const displayingFilters = useAtomValue(displayingFiltersAtom)
	const isCalibration = useAtomValue(isCalibrationAtom)
	const allPhotos = useAtomValue(sortedPhotosAtom)
	const photosCount = useAtomValue(displayGroupPhotosCountAtom)
	const currentProject = useAtomValue(projectAtom)
	const projectPaths = useAtomValue(projectPathsAtom)
	const camerasInfo = useAtomValue(allCamerasAtom)
	const fileFormatInfo = useAtomValue(allFileFormatsAtom)

	const showAdvancedSuggestedTooltip = useAtomValue(showingAdvancedSuggestedTooltipAtom)
	const showingCullingFilterTooltip = useAtomValue(showingCullingFilterTooltipAtom)
	const showingSmartCullingFiltersTooltip = useAtomValue(showingSmartCullingFiltersTooltipAtom)

	const [unsupportedPhotoTooltip, setUnsupportedPhotoTooltip] = useAtom(
		unsupportedPhotoTooltipAtom,
	)
	const [showingCullingHiddenTooltip, setShowingCullingHiddenTooltip] = useAtom(
		cullingHiddenPhotosTooltipAtom,
	)
	const isFilmstripExpanded = useAtomValue(filmstripExpandedAtom)
	const setShowingFilmstripExpander = useSetAtom(showingFilmstripExpanderAtom)

	const isManualMode = useAtomValue(isManualModeAtom)
	const isManualCulling = useAtomValue(isManualCullingAtom)
	const isCullingMode = useAtomValue(isCullingModeAtom)
	const isMetadataFinished = useAtomValue(isMetadataFinishedAtom)
	const metadataProgress = useAtomValue(metadataProgressAtom)
	const isExpressSetup = useAtomValue(isExpressSetupAtom)
	const isShowingKeepersOrRejects = useAtomValue(isShowingRejectsOrKeepersAtom)
	const isSuggestingKeepers = useAtomValue(isSuggestingKeepersAtom)
	const isShowingRejects = useAtomValue(isShowingRejectsAtom)

	const centeredEditingButtonsRef = useRef<HTMLDivElement | null>(null)
	const centeredCullingButtonsRef = useRef<HTMLDivElement | null>(null)
	const centerButtonWidth = useVisibleElementWidth([
		centeredEditingButtonsRef,
		centeredCullingButtonsRef,
	])

	useEffect(() => {
		if (!isSuggestingKeepers && displayGroup === "keepers") {
			setDisplayGroup("all")
		}
		if (!isShowingRejects && displayGroup === "rejects") {
			setDisplayGroup("all")
		}
		if (!isShowingKeepersOrRejects && displayGroup === "candidates") {
			setDisplayGroup("all")
		}
	}, [isShowingKeepersOrRejects, isSuggestingKeepers, isShowingRejects, displayGroup])

	const sortOptionDisplay = (option: SortOption) => t(sortOptionKeys[option])

	const renderUnsupported = () => {
		return (
			<FloatingTooltip
				open={unsupportedPhotoTooltip}
				side="bottom"
				sideOffset={16}
				tooltip={{
					main: t("bottomToolBar.unsupportedHidden"),
					action: t("bottomToolBar.gotIt"),
					actionHandler: () => {
						setUnsupportedPhotoTooltip(false)
					},
				}}
			>
				<Button
					variant="custom"
					size="custom"
					tooltip={t("bottomToolBar.tooltipUnsupported")}
					trackingLabel={
						displayGroup == "unsupported"
							? "unselect_unsupported"
							: "select_unsupported"
					}
					className={clsx(
						displayGroup == "unsupported" ? "text-base-100" : "text-base-500",
						"flex flex-col items-center hover:text-base-300",
					)}
					onClick={() => selectDisplayGroup("unsupported")}
				>
					<Icon name={"unsupported"} size={20} className="pb-1" />
					<div className="text-xxs">{photosCount.unsupported}</div>
				</Button>
			</FloatingTooltip>
		)
	}

	const updateSortOption = (value: SortOption) => {
		setSortOption(value)

		if (currentProject) {
			updateCurrentProject({ sortOption: value })
		}
	}

	const projectSubpaths = projectPaths.filter((info) => info.path.includes("/"))

	// After the project name is changed, the name of the root folder will no longer match the project name.
	const projectFolder = projectPaths.find((info) => !info.path.includes("/"))?.path

	const selectMetadataLoadingProgress = () => {
		return (
			<SelectLabel className="-mx-1 flex flex-row items-center justify-between border-b border-base-700 px-2 pb-2 pt-1 text-xs text-base-400">
				<div>{t("bottomToolBar.analyzingMetadata")}</div>
				<div>{`${metadataProgress}%`}</div>
			</SelectLabel>
		)
	}

	const dropdownMetadataLoadingProgress = () => {
		return (
			<DropdownMenuLabel className="-mx-1 flex flex-row items-center justify-between space-x-2 border-b border-base-700 px-2 pb-2 pt-1 text-xs text-base-400">
				<div>{t("bottomToolBar.analyzingMetadata")}</div>
				<div>{`${metadataProgress}%`}</div>
			</DropdownMenuLabel>
		)
	}

	const maxBreadcrumbWidth = `calc(50vw - ${centerButtonWidth / 2 + 306}px)` // 306 is the width of the left side of the toolbar, including the dropdown menus and buttons

	const renderDisplayGroupButton = (props: {
		key: DisplayGroup
		name: string
		count: number
		icon?: IconName
		customIcon?: ReactNode
		iconFill?: string
		isSpinner?: boolean
	}) => {
		const { key, name, icon, customIcon, iconFill, count, isSpinner } = props
		return (
			<Button
				trackingLabel={displayGroup === key ? `unselect_${key}` : `select_${key}`}
				variant="custom"
				size="custom"
				className={clsx(
					showAdvancedSuggestedTooltip && "rounded-sm border-2 border-accent-500",
					displayGroup == key
						? "rounded-md bg-base-300 text-base-900"
						: "rounded-md hover:bg-base-700",
				)}
				onClick={() => selectDisplayGroup(key)}
				tooltip={name}
			>
				<div className="flex flex-row items-center p-1.5">
					{customIcon}
					{!customIcon && icon && (
						<Icon size={15} name={icon} className={`mr-1 ${iconFill}`} />
					)}
					{isSpinner && <Spinner size={12} className="mr-1" />}
					<span className="mr-2 hidden font-medium xl:block">{name}</span>
					<span className="text-base-500">{count}</span>
				</div>
			</Button>
		)
	}
	const renderEditingButtons = () => {
		return (
			<div
				aria-label={t("bottomToolBar.ariaEditingFilters")}
				role="group"
				ref={centeredEditingButtonsRef}
				className="flex flex-row items-center justify-center space-x-1 text-xs"
			>
				{isCalibration && !isExpressSetup && (
					<FloatingTooltip
						open={showAdvancedSuggestedTooltip}
						side="bottom"
						sideOffset={16}
						tooltip={{
							title: t("bottomToolBar.suggestedPhotosTitle"),
							main: t("bottomToolBar.suggestedPhotosMain"),
							index: 2,
							totalCount: 2,
							onboardingKey: "calibrationTooltipsShown",
						}}
					>
						{renderDisplayGroupButton({
							key: "suggested",
							name: t(displayGroupKeys["suggested"]),
							icon: "suggested",
							iconFill: "fill-accent-600",
							count: photosCount.suggested,
						})}
					</FloatingTooltip>
				)}

				{renderDisplayGroupButton({
					key: "references",
					name: t(displayGroupKeys["references"]),
					icon: "diamond",
					count: photosCount.references,
				})}

				{!isCalibration &&
					renderDisplayGroupButton({
						key: "locked",
						name: t(displayGroupKeys["locked"]),
						icon: "locked",
						count: photosCount.locked,
					})}

				{!isCalibration &&
					renderDisplayGroupButton({
						key: "processed",
						name: t(displayGroupKeys["processed"]),
						icon: "check_circle",
						iconFill: "fill-green-500",
						count: photosCount.processed,
					})}

				{photosCount.processing > 0 &&
					renderDisplayGroupButton({
						key: "processing",
						name: t("bottomToolBar.processing"),
						isSpinner: true,
						count: photosCount.processing,
					})}
			</div>
		)
	}

	const renderCullingButtons = () => {
		if (isShowingKeepersOrRejects) {
			return (
				<div
					aria-label={t("bottomToolBar.ariaCullingFilters")}
					role="group"
					ref={centeredCullingButtonsRef}
					className="flex flex-row items-center justify-center space-x-1 text-xs"
				>
					{isSuggestingKeepers &&
						renderDisplayGroupButton({
							key: "keepers",
							name: t(displayGroupKeys["keepers"]),
							customIcon: <KeeperIcon size={15} className="mr-1" />,
							count: photosCount.keepers,
						})}
					<FloatingTooltip
						open={showingSmartCullingFiltersTooltip}
						side="bottom"
						sideOffset={16}
						tooltip={{
							title: t("bottomToolBar.smartCullingFiltersTitle"),
							main: t("bottomToolBar.smartCullingFiltersMain"),
							index: 2,
							totalCount: 4,
							onboardingKey: "cullingTooltipsShown",
						}}
					>
						{renderDisplayGroupButton({
							key: "candidates",
							name: t(displayGroupKeys["candidates"]),
							customIcon: <CandidateBadgeIcon size={15} className="mr-1" />,
							count: photosCount.candidates,
						})}
					</FloatingTooltip>
					{isShowingRejects &&
						renderDisplayGroupButton({
							key: "rejects",
							name: t(displayGroupKeys["rejects"]),
							customIcon: <RejectBadgeIcon size={15} className="mr-1" />,
							count: photosCount.rejects,
						})}

					{photosCount.cullingProcessing > 0 &&
						renderDisplayGroupButton({
							key: "cullingProcessing",
							name: t("bottomToolBar.processing"),
							isSpinner: true,
							count: photosCount.cullingProcessing,
						})}
				</div>
			)
		}
	}
	return (
		<div
			aria-label={t("bottomToolBar.ariaBottomToolbar")}
			className="no-scrollbar relative flex min-w-[800px] flex-row items-center justify-between overflow-scroll border-t border-base-900 p-1"
			onPointerEnter={() => {
				if (!isFilmstripExpanded) {
					setShowingFilmstripExpander(true)
				}
			}}
			onPointerLeave={() => {
				if (!isFilmstripExpanded) {
					setShowingFilmstripExpander(false)
				}
			}}
		>
			<div className="flex flex-row items-center space-x-1">
				<div
					role="group"
					aria-label={t("bottomToolBar.ariaSettingsAndHelp")}
					className="mr-2 flex flex-row items-center space-x-1"
				>
					<Button
						trackingLabel="open_settings"
						aria-label={t("bottomToolBar.ariaOpenSettings")}
						variant="icon"
						size="custom"
						onClick={() => replaceDialog(SettingsDialog)}
					>
						<Icon className="flex flex-none" size={18} name="settings" />
					</Button>
					<Button
						trackingLabel="open_shortcuts"
						aria-label={t("bottomToolBar.ariaOpenKeyboardShortcuts")}
						variant="icon"
						size="custom"
						onClick={() => replaceDialog(ShortCutsDialog)}
					>
						<Icon className="flex flex-none" name="keyboard" />
					</Button>
				</div>

				<div className="h-6 w-px shrink-0 bg-base-700" />

				<Select
					trackingContext="bottom_tool_bar"
					trackingLabel="sort_option"
					aria-label={t("bottomToolBar.ariaOpenFilmstripSortOptions")}
					value={sortOption}
					onValueChange={(value) => {
						updateSortOption(value)
					}}
				>
					<SelectTrigger
						aria-label={t("bottomToolBar.ariaOpenFilmstripSortingOrder")}
						className="px-1"
					>
						<Icon name="sort" size={20} />
					</SelectTrigger>

					<SelectContent
						side="top"
						aria-label={t("bottomToolBar.ariaFilmstripSortingOrderList")}
					>
						<SelectGroup>
							{!isMetadataFinished && selectMetadataLoadingProgress()}
							{SortOptions.map((option) => (
								<SelectItem
									aria-label={t("bottomToolBar.ariaFilmstripSortingOrderItem", {
										option: option.toWellFormed(),
									})}
									key={option}
									value={option}
								>
									{sortOptionDisplay(option)}
								</SelectItem>
							))}
						</SelectGroup>
					</SelectContent>
				</Select>

				<div className="h-6 w-px shrink-0 bg-base-700" />

				<PhotoBreadcrumb maxWidth={maxBreadcrumbWidth} />
			</div>

			{isCullingMode && renderCullingButtons()}

			{!isManualMode && !isCullingMode && renderEditingButtons()}

			<FloatingTooltip
				open={showingCullingFilterTooltip}
				side="bottom"
				sideOffset={16}
				tooltip={{
					title: t("bottomToolBar.additionalFiltersTitle"),
					main: t("bottomToolBar.additionalFiltersMain"),
					index: isManualCulling ? 2 : 3,
					totalCount: isManualCulling ? 3 : 4,
					onboardingKey: isManualCulling
						? "manualCullingTooltipsShown"
						: "cullingTooltipsShown",
				}}
			>
				<div className="flex flex-row items-center space-x-2.5 p-1">
					{displayingFilters.has("flag") && (
						<div
							aria-label={t("bottomToolBar.ariaPhotoFlaggedRejectedFilters")}
							role="group"
							className="flex flex-row items-center space-x-2.5"
						>
							<FlagFilter flag="flagged" />
							<FlagFilter flag="unflagged" />
							<FlagFilter flag="rejected" />
							<div className="h-6 w-px shrink-0 bg-base-700" />
						</div>
					)}
					{photosCount.unsupported > 0 && renderUnsupported()}

					{displayingFilters.has("color") && (
						<div
							aria-label={t("bottomToolBar.ariaPhotoColorFilters")}
							role="group"
							className="flex flex-row items-center space-x-2.5"
						>
							<ColorFilter color="No Color" />{" "}
							{/* i18n-ignore: ColorTag enum value */}
							<ColorFilter color="Red" />
							<ColorFilter color="Yellow" />
							<ColorFilter color="Green" />
							<ColorFilter color="Blue" />
							<ColorFilter color="Purple" />
							<div className="h-6 w-px shrink-0 bg-base-700" />
						</div>
					)}

					{displayingFilters.has("rating") && (
						<div
							aria-label={t("bottomToolBar.ariaPhotoRatingFilters")}
							role="group"
							className="flex flex-row items-center space-x-2.5"
						>
							<RatingFilter rating={0} />
							<RatingFilter rating={1} />
							<RatingFilter rating={2} />
							<RatingFilter rating={3} />
							<RatingFilter rating={4} />
							<RatingFilter rating={5} />
							<div className="h-6 w-px shrink-0 bg-base-700" />
						</div>
					)}

					{displayingFilters.has("visibility") && (
						<div
							aria-label={t("bottomToolBar.ariaPhotoVisibilityFilters")}
							role="group"
							className="flex flex-row items-center space-x-2.5"
						>
							<VisibilityFilter visibility="visible" />
							<FloatingTooltip
								open={showingCullingHiddenTooltip}
								side="bottom"
								sideOffset={16}
								tooltip={{
									title: t("bottomToolBar.hiddenPhotosTitle"),
									main: t("bottomToolBar.hiddenPhotosMain"),
									action: t("bottomToolBar.gotIt"),
									actionHandler: () => {
										markOnboardingShown("hiddenPhotosTooltipShown")
										setShowingCullingHiddenTooltip(false)
									},
								}}
							>
								<div>
									<VisibilityFilter visibility="hidden" />
								</div>
							</FloatingTooltip>
							<div className="h-6 w-px shrink-0 bg-base-700" />
						</div>
					)}

					{displayingFilters.has("folder") && (
						<DropdownMenu>
							<DropdownMenuTrigger
								aria-label={t("bottomToolBar.ariaOpenPhotoPathFiltersList")}
								className="flex flex-row items-center space-x-1 text-base-300 hover:text-base-100 disabled:opacity-50"
							>
								<Icon name="folder" size={20} />
							</DropdownMenuTrigger>
							<DropdownMenuContent
								aria-label={t("bottomToolBar.ariaPhotoPathFiltersList")}
							>
								{currentProject && projectFolder && (
									<FolderSelector
										pathInfo={{
											path: projectFolder,
											photoCount: currentProject.photoCount ?? 0,
										}}
									/>
								)}

								{projectSubpaths.length > 0 && <DropdownMenuSeparator />}
								{projectSubpaths.map((pathInfo) => (
									<FolderSelector pathInfo={pathInfo} key={pathInfo.path} />
								))}
							</DropdownMenuContent>
						</DropdownMenu>
					)}

					{displayingFilters.has("file type") && (
						<DropdownMenu>
							<DropdownMenuTrigger
								aria-label={t("bottomToolBar.ariaOpenPhotoFileTypeFiltersList")}
								className="flex flex-row items-center space-x-1 text-base-300 hover:text-base-100 disabled:opacity-50"
							>
								<Icon name="format" size={20} />
							</DropdownMenuTrigger>
							<DropdownMenuContent
								aria-label={t("bottomToolBar.ariaPhotoFileTypeFiltersList")}
							>
								{!isMetadataFinished && dropdownMetadataLoadingProgress()}
								<FileFormatSelector format="all" count={allPhotos.length} />
								<DropdownMenuSeparator />
								{Object.keys(fileFormatInfo).map((key) => (
									<FileFormatSelector
										key={key}
										format={key}
										count={fileFormatInfo[key] ?? 0}
									/>
								))}
							</DropdownMenuContent>
						</DropdownMenu>
					)}

					{displayingFilters.has("camera model") && (
						<DropdownMenu>
							<DropdownMenuTrigger
								aria-label={t("bottomToolBar.ariaOpenPhotoCameraModelFiltersList")}
								className="flex flex-row items-center space-x-1 text-base-300 hover:text-base-100 disabled:opacity-50"
							>
								<Icon name="camera" size={20} />
							</DropdownMenuTrigger>
							<DropdownMenuContent
								aria-label={t("bottomToolBar.ariaPhotoCameraModelFiltersList")}
								className="max-h-[80vh] !overflow-auto"
							>
								{!isMetadataFinished && dropdownMetadataLoadingProgress()}
								<CameraSelector camera="all" count={allPhotos.length} />
								<DropdownMenuSeparator />
								{Object.keys(camerasInfo).map((key) => (
									<CameraSelector
										key={key}
										camera={key}
										count={camerasInfo[key] ?? 0}
									/>
								))}
							</DropdownMenuContent>
						</DropdownMenu>
					)}

					{(displayingFilters.has("camera model") ||
						displayingFilters.has("file type") ||
						displayingFilters.has("folder")) && (
						<div className="h-6 w-px shrink-0 bg-base-700" />
					)}

					<DropdownMenu>
						<DropdownMenuTrigger
							aria-label={t("bottomToolBar.ariaPhotoDisplayedFiltersList")}
							className="flex flex-row items-center space-x-1 text-base-300 hover:text-base-100 disabled:opacity-50"
						>
							<Icon name="filter" size={20} />
						</DropdownMenuTrigger>
						<DropdownMenuContent
							aria-label={t("bottomToolBar.ariaDisplayedFiltersList")}
							className="max-h-[80vh] !overflow-auto"
						>
							<DisplayingFilterSelector filter={"flag"} />
							<DisplayingFilterSelector filter={"color"} />
							<DisplayingFilterSelector filter={"rating"} />
							<DisplayingFilterSelector filter={"visibility"} />
							<DropdownMenuSeparator />
							<DisplayingFilterSelector filter={"folder"} />
							<DisplayingFilterSelector filter={"file type"} />
							<DisplayingFilterSelector filter={"camera model"} />
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			</FloatingTooltip>
		</div>
	)
}

const DisplayingFilterSelector = (props: { filter: FilterType }) => {
	const { t } = useTranslation("components")
	const filter = props.filter
	const [displayingFilters, setDisplayingFilters] = useAtom(displayingFiltersAtom)

	const isSelected = displayingFilters.has(filter)

	return (
		<DropdownMenuItem
			trackingLabel={isSelected ? "unselect_path_filter" : "select_path_filter"}
			trackingValue={filter}
			key={filter}
			onClick={(e) => {
				e.preventDefault()
				if (isSelected) {
					setDisplayingFilters((prev) => {
						const newSet = new Set(prev)
						newSet.delete(filter)
						return newSet
					})
				} else {
					setDisplayingFilters((prev) => {
						const newSet = new Set(prev)
						newSet.add(filter)
						return newSet
					})
				}
			}}
		>
			<div
				className={clsx(
					isSelected ? "text-base-100" : "text-base-500",
					"flex w-full flex-row justify-between space-x-4",
				)}
			>
				<div className="flex flex-row space-x-2">
					<Checkbox checked={isSelected} />
					<div className="max-w-[242px] truncate text-xs">
						{t(displayingFilterKeys[filter])}
					</div>
				</div>
			</div>
		</DropdownMenuItem>
	)
}

const FolderSelector = (props: { pathInfo: PathInfo }) => {
	const { t } = useTranslation("components")
	const [pathFilters] = useAtom(pathFiltersAtom)
	const pathInfo = props.pathInfo

	const displayFolderName = () => {
		const name = pathInfo.path.split("/").at(-1)
		if (name === "Sample Project") {
			return t("toolbar.sampleProject")
		}
		return name
	}

	const isRootFolder = () => {
		return pathInfo.path.split("/").length === 1
	}

	const isSelected = pathFilters.has(pathInfo.path)

	return (
		<DropdownMenuItem
			trackingLabel={isSelected ? "unselect_path_filter" : "select_path_filter"}
			trackingValue={pathInfo.path}
			key={pathInfo.path}
			onClick={(e) => {
				e.preventDefault()
				selectPathFilter(pathInfo.path)
			}}
		>
			<div
				className={clsx(
					!isRootFolder() && "pl-2",
					isSelected ? "text-base-100" : "text-base-500",
					"flex w-full flex-row justify-between space-x-4",
				)}
			>
				<div className="flex flex-row space-x-2">
					<Checkbox checked={isSelected} />
					<div className="max-w-[242px] truncate text-xs">{displayFolderName()}</div>
				</div>
				<div className="text-xs">{pathInfo.photoCount}</div>
			</div>
		</DropdownMenuItem>
	)
}

const CameraSelector = (props: { camera: string; count: number }) => {
	const { t } = useTranslation("components")
	const [cameraFilters] = useAtom(cameraFiltersAtom)

	const isSelected = () => {
		if (props.camera === "all") {
			return cameraFilters.size === 0
		}
		return !cameraFilters.has(props.camera)
	}
	return (
		<DropdownMenuItem
			trackingLabel={isSelected() ? "unselect_camera_filter" : "select_camera_filter"}
			trackingValue={props.camera}
			key={props.camera}
			onClick={(e) => {
				e.preventDefault()
				selectCameraFilter(props.camera)
			}}
		>
			<div
				className={clsx(
					isSelected() ? "text-base-100" : "text-base-500",
					"flex w-full flex-row justify-between space-x-4",
				)}
			>
				<div className="flex flex-row space-x-2">
					<Checkbox checked={isSelected()} />
					<div className="max-w-[242px] truncate text-xs">
						{props.camera === "all" ? t("bottomToolBar.allCameras") : props.camera}
					</div>
				</div>
				<div className="text-xs">{props.count}</div>
			</div>
		</DropdownMenuItem>
	)
}

const FileFormatSelector = (props: { format: string; count: number }) => {
	const { t } = useTranslation("components")
	const [fileFormatFilters] = useAtom(fileFormatFiltersAtom)

	const isSelected = () => {
		if (props.format === "all") {
			return fileFormatFilters.size === 0
		}
		return !fileFormatFilters.has(props.format)
	}
	return (
		<DropdownMenuItem
			trackingLabel={isSelected() ? "unselect_format_filter" : "select_format_filter"}
			trackingValue={props.format}
			key={props.format}
			onClick={(e) => {
				e.preventDefault()
				selectFileFormatFilter(props.format)
			}}
		>
			<div
				className={clsx(
					isSelected() ? "text-base-100" : "text-base-500",
					"flex w-full flex-row justify-between space-x-4",
				)}
			>
				<div className="flex flex-row space-x-2">
					<Checkbox checked={isSelected()} />
					<div className="max-w-[242px] truncate text-xs">
						{props.format === "all" ? t("bottomToolBar.allPhotos") : props.format}
					</div>
				</div>
				<div className="text-xs">{props.count}</div>
			</div>
		</DropdownMenuItem>
	)
}

const VisibilityFilter = (props: { visibility: Visibility }) => {
	const { t } = useTranslation("components")
	const visibility = props.visibility
	const [visibilityFilters] = useAtom(visibilityFiltersAtom)
	const [visibilityPhotosCount] = useAtom(visibilityPhotosCountAtom)

	const isSelected = visibilityFilters.has(visibility)
	return (
		<Button
			variant="custom"
			size="custom"
			tooltip={t(visibilityFilterKeys[visibility])}
			data-testid={`visibility_filter_${visibility}`}
			trackingLabel={
				isSelected ? `unselect_${visibility}_filter` : `select_${visibility}_filter`
			}
			className={clsx(
				isSelected ? "text-base-100" : "text-base-500",
				"flex flex-col items-center hover:text-base-300",
			)}
			onClick={() => {
				selectVisibilityFilter(visibility)
			}}
		>
			<Icon name={visibility as IconName} size={20} />
			<div className="text-xxs">{visibilityPhotosCount[visibility]}</div>
		</Button>
	)
}

const FlagFilter = (props: { flag: PhotoFlagStatus }) => {
	const { t } = useTranslation("components")
	const [flagFilters] = useAtom(flagFiltersAtom)
	const [flagPhotosCount] = useAtom(flagPhotosCountAtom)

	const isSelected = flagFilters.has(props.flag)

	return (
		<Button
			variant="custom"
			size="custom"
			tooltip={t(photoFlagKeys[props.flag])}
			data-testid={`flag_filter_${props.flag}`}
			trackingLabel={isSelected ? "unselect_flag_filter" : "select_flag_filter"}
			trackingValue={String(props.flag)}
			className={clsx(
				isSelected ? "text-base-100" : "text-base-500",
				"flex flex-col items-center hover:text-base-300",
			)}
			onClick={() => {
				selectFlagFilter(props.flag)
			}}
		>
			<Icon name={props.flag} size={18} />
			<div className="text-xxs">{flagPhotosCount[props.flag]}</div>
		</Button>
	)
}

const RatingFilter = (props: { rating: Rating }) => {
	const { t } = useTranslation("components")
	const rating = props.rating
	const [ratingFilters] = useAtom(ratingFiltersAtom)
	const [ratingPhotosCount] = useAtom(starRatingPhotosCountAtom)

	const isSelected = ratingFilters.has(rating)
	return (
		<Button
			variant="custom"
			size="custom"
			tooltip={
				rating === 0
					? t("bottomToolBar.noStars")
					: t("bottomToolBar.ratingStars", { count: rating })
			}
			data-testid={`rating_filter_${rating}`}
			trackingLabel={isSelected ? "unselect_rating_filter" : "select_rating_filter"}
			trackingValue={String(props.rating)}
			className={clsx(
				isSelected ? "text-base-100" : "text-base-500",
				"flex flex-col items-center hover:text-base-300",
			)}
			onClick={() => {
				selectRatingFilter(rating)
			}}
		>
			<Icon name={isSelected ? `star${rating}_filled` : `star${rating}`} size={20} />
			<div className="text-xxs">{ratingPhotosCount[rating]}</div>
		</Button>
	)
}

const ColorFilter = (props: { color: ColorTag }) => {
	const { t } = useTranslation("components")
	const color = props.color
	const [colorFilters] = useAtom(colorFiltersAtom)
	const [colorPhotosCount] = useAtom(colorPhotosCountAtom)

	const isSelected = colorFilters.has(color)

	const displayColor = () => {
		switch (color) {
			case "Red":
				return isSelected ? "bg-red-500" : "border-red-500"
			case "Yellow":
				return isSelected ? "bg-yellow-500" : "border-yellow-500 "
			case "Green":
				return isSelected ? "bg-green-500" : "border-green-500 "
			case "Blue":
				return isSelected ? "bg-blue-500" : "border-blue-500 "
			case "Purple":
				return isSelected ? "bg-purple-500" : "border-purple-500"
		}
	}
	return (
		<Button
			trackingLabel={isSelected ? "unselect_color_filter" : "select_color_filter"}
			trackingValue={props.color}
			data-testid={`color_filter_${color.toLowerCase().replace(" ", "_")}`}
			variant="custom"
			size="custom"
			tooltip={t(colorTagKeys[color])}
			className={clsx(
				isSelected ? "text-base-100" : "text-base-300 opacity-50",
				"flex flex-col items-center space-y-1 hover:opacity-100",
			)}
			onClick={() => {
				selectColorFilter(color)
			}}
		>
			{color === "No Color" && (
				<Icon
					className="-m-px"
					name={isSelected ? "no_color_filled" : "no_color"}
					size={18}
				/>
			)}
			{color !== "No Color" && (
				<div
					className={clsx(
						isSelected ? "border-0" : "border-2",
						displayColor(),
						"size-4 rounded-full",
					)}
				></div>
			)}
			<div className="text-xxs">{colorPhotosCount[color]}</div>
		</Button>
	)
}
